import React, { Component } from "react";
import PropTypes from "prop-types";
import DropDownButton from "components/atoms/DropDownButton";
import ToolTip from "components/atoms/ToolTip";
import FilterBox from "components/molecules/FilterBox";
import makeToggleable from "hoc/toggleable";
import makePersistMountedState from "hoc/mountedState";
import makeScrolling from "hoc/scrolling";
import ShowHide from "containers/behavior/ShowHide";
import Panels from "components/molecules/Panels";
import classnames from "classnames";
import { isDeepEqual } from "utils/predicates";
import { customTypeChecker } from "utils/propType";
import { debugModeArgument } from "utils/debugModeArgument";

@makePersistMountedState
@makeToggleable({ toggleProp: "tooltipIsActive" })
@makeToggleable({ toggleProp: "dropdownIsActive" })
@makeScrolling({ refName: "container" })
export default class CascadeFilter extends Component {
  static propTypes = {
    className: PropTypes.string,
    columnCount: customTypeChecker(
      (columns) => columns > 0 && columns < 5,
      "Integer between 1 and 4",
      true,
    ),
    currentValues: PropTypes.array.isRequired,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        count: PropTypes.number,
        data: PropTypes.array,
      }),
    ).isRequired,
    dropdownIsActive: PropTypes.bool,
    forceActive: PropTypes.bool,
    filteredValues: PropTypes.array.isRequired,
    label: PropTypes.string.isRequired,
    loadingLevel: PropTypes.number,
    name: PropTypes.string.isRequired,
    onFilterChange: PropTypes.func.isRequired,
    onMountStateChange: PropTypes.func.isRequired,
    onToggleDropdownIsActive: PropTypes.func,
    onToggleTooltipIsActive: PropTypes.func,
    scrollToTop: PropTypes.func,
    tooltipIsActive: PropTypes.bool,
  };

  static defaultProps = {
    currentValues: [],
    data: [],
    columnCount: 1,
    forceActive: false,
  };

  state = {
    selectedValues: this.props.currentValues,
    prevCurrentValues: this.props.currentValues,
    dropDownBtn: null,
  };

  static getDerivedStateFromProps(props, state) {
    if (!isDeepEqual(props.currentValues, state.prevCurrentValues)) {
      return {
        selectedValues: props.currentValues,
        prevCurrentValues: props.currentValues,
      };
    }

    return state;
  }

  handleClose = () => {
    if (this.state.dropDownBtn) {
      this.state.dropDownBtn.setAttribute("aria-expanded", "false");
      this.setState({ dropDownBtn: null });
    }
    this.props.onToggleDropdownIsActive(false);
    document.removeEventListener("mousedown", this.handleClickOutside);
    document.removeEventListener("keydown", this.handleEscapeKey);
  };

  handleEscapeKey = (event) => {
    if (event.key === "Escape" || event.key === "Esc") {
      this.handleClose();
    }
  };

  closeAllFilters = () => {
    const escapeEvent = new KeyboardEvent("keydown", {
      key: "Escape",
      code: "Escape",
      keyCode: 27,
      which: 27,
      bubbles: true,
      cancelable: true,
    });

    document.dispatchEvent(escapeEvent);
  };

  handleOpen = (dropDownBtn) => {
    this.setState({ dropDownBtn });
    dropDownBtn.setAttribute("aria-expanded", "true");
    this.closeAllFilters();
    this.props.scrollToTop();
    this.props.onToggleDropdownIsActive(true);
    document.addEventListener("mousedown", this.handleClickOutside);
    document.addEventListener("keydown", this.handleEscapeKey);
  };

  resetSelectedValues = () => {
    this.setState({ selectedValues: this.props.filteredValues });
  };

  handleFilterValueChange = (value, currentLevel) => {
    const { columnCount } = this.props;

    this.props.onFilterChange(this.props.name, value ? [value] : []);

    this.setState({ selectedValues: value ? [value] : [] });
    if (columnCount === currentLevel) {
      this.handleClose();
    }
  };

  handleDropDownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { data, dropdownIsActive } = this.props;
    if (data.length === 0) {
      return;
    }

    const dropDownBtn = e.target.closest("button");

    if (dropdownIsActive) {
      this.resetSelectedValues();
      this.handleClose();
    } else {
      this.handleOpen(dropDownBtn);
    }
  };

  handleClickOutside = (ev) => {
    // eslint-disable-next-line react/no-string-refs
    const { container } = this.refs;
    const { target } = ev;

    if (target !== container && !container.contains(target)) {
      this.resetSelectedValues();
      this.handleClose();
    }
  };

  toggleTooltip = (e) => {
    if (
      (e.type === "mouseenter" || e.type === "mouseleave") &&
      e.target.contains(document.activeElement)
    ) {
      return false;
    } else if (
      e.type === "mouseleave" &&
      this.props.tooltipIsActive === false
    ) {
      return false;
    }

    const { onToggleTooltipIsActive } = this.props;

    if (this.props.tooltipIsActive) {
      onToggleTooltipIsActive(false);
    } else {
      onToggleTooltipIsActive(true);
    }
  };
  render() {
    const {
      data,
      name,
      label,
      loadingLevel,
      dropdownIsActive,
      forceActive,
      tooltipIsActive,
      className,
      filteredValues,
      columnCount,
      currentValues,
    } = this.props;

    const { selectedValues } = this.state;
    const hasData = data.length > 0;
    const componentBaseClass = "sal-cascade-filter";
    const componentClassName = classnames(componentBaseClass, className, {
      "sal-cascade-filter--hidden": !hasData && !forceActive,
    });
    const dropdownButtonClass = `${componentBaseClass}__button-dropdown`;
    const filterBoxClasses = classnames(`${componentBaseClass}__filterbox`, {
      [`${componentBaseClass}__filterbox--size-${columnCount}`]:
        columnCount > 1,
    });

    let dropDownClassName = dropdownIsActive
      ? `${dropdownButtonClass} ${dropdownButtonClass}--active`
      : dropdownButtonClass;

    return (
      /* eslint-disable react/no-string-refs */
      <div
        className={componentClassName}
        ref="container"
        data-cv-debug={debugModeArgument("true")}
        data-cv-debug-system={debugModeArgument("SAL")}
        data-cv-debug-component={debugModeArgument("CascadeFilter")}
      >
        {!hasData ? (
          <div
            className={`${componentBaseClass}__button-wrapper`}
            onMouseEnter={this.toggleTooltip}
            onMouseLeave={this.toggleTooltip}
            onFocus={this.toggleTooltip}
            onBlur={this.toggleTooltip}
          >
            <DropDownButton
              name={name}
              value="open"
              onClick={this.handleDropDownClick}
              aria-expanded="false"
              aria-disabled={!hasData}
              aria-describedby={!hasData ? `${name}__tooltip` : null}
              caption={label}
              hasSelectedValue={currentValues && currentValues.length > 0}
              className={dropDownClassName}
              dropdownActive={dropdownIsActive}
            />
          </div>
        ) : (
          <DropDownButton
            name={name}
            value="open"
            onClick={this.handleDropDownClick}
            aria-expanded="false"
            aria-disabled={!hasData}
            aria-describedby={!hasData ? `${name}__tooltip` : null}
            caption={label}
            hasSelectedValue={currentValues && currentValues.length > 0}
            className={dropDownClassName}
            dropdownActive={dropdownIsActive}
          />
        )}
        {!hasData && (
          <ToolTip
            id={`${name}__tooltip`}
            group="filter"
            isActive={tooltipIsActive}
            position="top"
            className={`${componentBaseClass}__tooltip`}
          >
            In dieser Filterkombination nicht wählbar.
          </ToolTip>
        )}
        {
          <ShowHide
            isVisible={dropdownIsActive}
            animationType="slideDown"
            className={componentBaseClass + "__dropdown-container"}
            render={({ isVisible }) => (
              <FilterBox
                className={filterBoxClasses}
                selectedValues={selectedValues}
                currentValues={filteredValues}
                onCloseClick={this.handleClose}
                hasBoxShadow={isVisible}
                render={(additionalProps) => (
                  <Panels
                    name={name}
                    data={data}
                    onFilterValueChange={this.handleFilterValueChange}
                    selectedValues={selectedValues}
                    columnCount={columnCount}
                    panelClass={`${componentBaseClass}__panel`}
                    loadingLevel={loadingLevel}
                    {...additionalProps}
                  />
                )}
              />
            )}
          />
        }
      </div>
    );
  }
}
